import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "Rieave Editorial", tags: [], is_published: true, seo_title: "", seo_description: "" };

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/blogs").then(r => setBlogs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...editing, tags: Array.isArray(editing.tags) ? editing.tags : (editing.tags || "").split(",").map(t => t.trim()).filter(Boolean) };
      if (editing.id) await api.patch(`/admin/blogs/${editing.id}`, body);
      else await api.post(`/admin/blogs`, body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/admin/blogs/${b.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-blogs-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div><p className="eyebrow mb-1">Content</p><h1 className="font-hero text-3xl">Journal</h1></div>
        <button className="btn-primary" onClick={() => { setEditing({ ...empty }); setOpen(true); }}><Plus size={14} className="mr-2" /> New post</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {blogs.map(b => (
          <div key={b.id} className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="aspect-[4/3] bg-blush/40">{b.cover_image && <img src={b.cover_image} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <p className="font-cormorant text-lg leading-tight">{b.title}</p>
              <p className="text-xs text-charcoal/70 mb-3">/blog/{b.slug}</p>
              <div className="flex gap-2">
                <button className="text-charcoal" onClick={() => { setEditing({ ...b, tags: (b.tags || []).join(", ") }); setOpen(true); }} aria-label="edit"><Pencil size={14} /></button>
                <button className="text-rose" onClick={() => remove(b)} aria-label="delete"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : setOpen(false))}>
        <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-hero text-2xl">{editing?.id ? "Edit" : "New"} post</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div><label className="eyebrow">Title</label><Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><label className="eyebrow">Slug</label><Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} /></div>
              </div>
              <div><label className="eyebrow">Cover image URL</label><Input value={editing.cover_image || ""} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} /></div>
              <div><label className="eyebrow">Excerpt</label><Input value={editing.excerpt || ""} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} /></div>
              <div><label className="eyebrow">Content (markdown friendly)</label><Textarea rows={10} value={editing.content || ""} onChange={e => setEditing({ ...editing, content: e.target.value })} /></div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><label className="eyebrow">Author</label><Input value={editing.author || ""} onChange={e => setEditing({ ...editing, author: e.target.value })} /></div>
                <div><label className="eyebrow">Tags (comma)</label><Input value={typeof editing.tags === "string" ? editing.tags : (editing.tags || []).join(", ")} onChange={e => setEditing({ ...editing, tags: e.target.value })} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><label className="eyebrow">SEO title</label><Input value={editing.seo_title || ""} onChange={e => setEditing({ ...editing, seo_title: e.target.value })} /></div>
                <div><label className="eyebrow">SEO description</label><Input value={editing.seo_description || ""} onChange={e => setEditing({ ...editing, seo_description: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 font-label text-[12px] uppercase tracking-[0.2em]">
                <input type="checkbox" checked={editing.is_published} onChange={e => setEditing({ ...editing, is_published: e.target.checked })} className="accent-rose" /> Published
              </label>
              <div className="flex justify-end gap-3 pt-3">
                <button className="btn-outline" onClick={() => setOpen(false)}>Cancel</button>
                <button className="btn-primary" onClick={save}>Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogs;
